import type { ReactNode } from 'react';
import { Button, useModalRequestClose } from '@safetyhub/ui';
import { DirtyHint } from './DirtyHint';

/**
 * 수정 모달 푸터 골격 — `useModalRequestClose`(취소가 미저장 닫기 확인을 타도록) + 변경 힌트 +
 * 저장 버튼이 6곳에 복제돼 있던 것을 흡수한다.
 *
 * 규약(base Modal):
 * - 취소는 항상 `requestClose ?? onFallbackClose`로 수렴해야 dirty 닫기 확인을 탄다. onClose를
 *   직접 부르면 확인을 우회한다. `useModalRequestClose`는 Modal children/footer의 **하위
 *   컴포넌트**에서만 잡히므로 이 컴포넌트가 그 자리를 차지한다(호출부는 `footer` prop에 이걸 렌더).
 * - 이 컴포넌트는 항상 fragment(힌트+취소+저장)만 낸다 — 구분선·간격·고정 배치는 base `Modal`의
 *   `footer` 자리가 소유한다. ⛔ **감싸는 래퍼를 다시 만들지 말 것**(그러면 구분선이 두 겹이 되고
 *   children 안에 두는 옛 형태로 되돌아간다 — 그 경위는 `components/Modal.tsx`의 그 자리 주석).
 *
 * 힌트: 기본은 `DirtyHint`(변경 있음/없음). `blockedReason`을 주면 그 문장을 대신 표시한다
 * (저장이 왜 막혔는지까지 말하는 폼용 — 이름 미입력·번호 오류 등 dirty보다 풍부한 메시지).
 *
 * 저장 버튼: `submitType='submit'`이면 폼 네이티브 제출(Enter 포함)에 맡기고 onClick을 달지 않는다.
 * `'button'`이면 `onSubmit`을 onClick으로 건다. `submitVariant`로 success 등 색을 바꾼다.
 *
 * ⛔ **`submitType='submit'`을 `<Modal footer={…}>`에서 쓰려면 `submitForm`이 «필수»다.** base
 * Modal은 본문과 푸터를 형제 div로 렌더하므로 푸터는 children의 `<form>` 밖이고, 그러면 제출이
 * **조용히 끊긴다**(버튼은 멀쩡해 보이고 tsc·spec 어느 것도 못 본다). children의 폼에 `id`를 주고
 * 그 값을 `submitForm`으로 넘길 것 — 그 짝을 재는 탐지기는
 * `apps/web/src/components/__tests__/modalFooterSubmitFormGuard.test.ts`다.
 */
export function EditModalFooter({
  isDirty,
  showHint = true,
  blockedReason,
  submitLabel,
  submitType = 'button',
  submitForm,
  submitVariant = 'primary',
  submitDisabled,
  busy = false,
  busyLabel = '저장 중...',
  onSubmit,
  onFallbackClose,
}: {
  isDirty: boolean;
  /** 힌트 표시 여부(조회 전용/생성 모드에서 끔). blockedReason 모드면 무시된다. */
  showHint?: boolean;
  /**
   * 저장이 막힌 이유 문장. 주면 DirtyHint 대신 이 문장을 role=status로 표시한다
   * (빈 문자열/undefined면 표시 안 함). "변경사항이 없습니다."까지 포함하는 폼용.
   */
  blockedReason?: string;
  submitLabel: string;
  submitType?: 'button' | 'submit';
  /**
   * 제출할 폼의 `id`(HTML5 `form` 속성). 푸터가 그 폼 **밖**에 있을 때 — `<Modal footer={…}>`가
   * 항상 그렇다 — 이것 없이는 `submitType='submit'`이 아무 일도 하지 않는다.
   */
  submitForm?: string;
  submitVariant?: 'primary' | 'success' | 'secondary' | 'danger';
  submitDisabled: boolean;
  busy?: boolean;
  busyLabel?: string;
  onSubmit?: () => void;
  onFallbackClose: () => void;
}) {
  const requestClose = useModalRequestClose();

  let hint: ReactNode = null;
  if (blockedReason !== undefined) {
    hint =
      blockedReason && !busy ? (
        <p className="mr-auto self-center text-xs text-ink-500" role="status">
          {blockedReason}
        </p>
      ) : null;
  } else if (showHint) {
    hint = <DirtyHint isDirty={isDirty} className="mr-auto self-center text-sm" />;
  }

  return (
    <>
      {hint}
      <Button
        variant="secondary"
        type="button"
        onClick={() => (requestClose ?? onFallbackClose)()}
        disabled={busy}
      >
        취소
      </Button>
      <Button
        variant={submitVariant}
        type={submitType}
        form={submitType === 'submit' ? submitForm : undefined}
        onClick={submitType === 'submit' ? undefined : onSubmit}
        disabled={submitDisabled}
      >
        {busy ? busyLabel : submitLabel}
      </Button>
    </>
  );
}
